import {
  FeatureFlagsContext,
  FormattedMarkupWrapperProps,
} from '@tableau/data-catalog-shared';
import { TextFormatterDisplayWidget } from '@tableau/widgets';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  NonDatasourceFieldDetailsFragment,
  NonDatasourceFieldDetailsFragment_ColumnField_columnsConnection_nodes,
  // tslint:disable-next-line:max-line-length
  NonDatasourceFieldDetailsFragment_ColumnField_columnsConnection_nodes_referencedByFlowColumnOutputField_parentFields_FlowColumnInputField_column,
} from '../../../gen-src/queries/NonDatasourceFieldDetailsFragment';
import { SheetFieldInstanceFragment_DatasourceField_remoteField } from '../../../gen-src/queries/SheetFieldInstanceFragment';
import { DescriptionInfo } from '../fieldColumn/DescriptionInfo';
import { SheetField } from './SheetField';
import {
  FieldCardDetailMarkupWrapper,
  FieldCardDetailText,
  FieldCardDetailTitle,
  FieldCardDetailTitleWrapper,
  NoDescriptionPlaceholder,
} from './VizClientFieldCardStyles';


type Column = NonDatasourceFieldDetailsFragment_ColumnField_columnsConnection_nodes;
// tslint:disable-next-line:max-line-length
type UpstreamColumn = NonDatasourceFieldDetailsFragment_ColumnField_columnsConnection_nodes_referencedByFlowColumnOutputField_parentFields_FlowColumnInputField_column;
type Field = SheetFieldInstanceFragment_DatasourceField_remoteField;

export type VizClientFieldCardDescriptionProps = {
  readonly field: SheetField;
};

export function VizClientFieldCardDescription(props: VizClientFieldCardDescriptionProps): JSX.Element {
  const { markupEditingEnabled } = React.useContext(FeatureFlagsContext); const field = props.field;

  if (field.description) {
    return <DirectDescription description={field.description} markupEditingEnabled={markupEditingEnabled} />;
  }
  const column = maybeGetColumnFromField(field);
  if (column) {
    if (column.description) {
      return <ColumnDescription column={column} markupEditingEnabled={markupEditingEnabled} />;
    }
    if (column.referencedByFlowColumnOutputField?.[0]?.parentFields?.[0]?.__typename === 'FlowColumnInputField') {
      const upstreamColumn = column.referencedByFlowColumnOutputField?.[0]?.parentFields?.[0]?.column;
      if (upstreamColumn?.description) {
        return <ColumnDescription column={upstreamColumn} markupEditingEnabled={markupEditingEnabled} />;
      }
    }
  }
  if (field.__typename === 'DatasourceField') {
    const remoteField = field.remoteField;
    if (remoteField) {
      if (remoteField.description) {
        return <RemoteFieldDescription remoteField={remoteField} markupEditingEnabled={markupEditingEnabled} />;
      }
      const remoteColumn = maybeGetColumnFromField(remoteField);
      if (remoteColumn && remoteColumn.description) {
        return (
          <RemoteColumnDescription
            remoteColumn={remoteColumn}
            remoteField={remoteField}
            markupEditingEnabled={markupEditingEnabled}
          />
        );
      }
      if (remoteField.referencedByFlowFieldOutputField?.[0]?.parentFields?.[0]?.__typename === 'FlowColumnInputField') {
        const remoteUpstreamColumn = remoteField.referencedByFlowFieldOutputField?.[0]?.parentFields?.[0]?.column;
        if (remoteUpstreamColumn?.description) {
          return (
            <RemoteColumnDescription
              remoteColumn={remoteUpstreamColumn}
              remoteField={remoteField}
              markupEditingEnabled={markupEditingEnabled}
            />
          );
        }
      }
    }
  }
  return <NoDescription />;
}

function FieldCardDetailMarkup(props: FormattedMarkupWrapperProps): JSX.Element | null {
  const { linksDisabled, ...restProps } = props;
  return (
    <FieldCardDetailMarkupWrapper>
      <TextFormatterDisplayWidget {...restProps} />
    </FieldCardDetailMarkupWrapper>
  );
}

function DirectDescription(props: { description: string, markupEditingEnabled: boolean }): JSX.Element {
  return (
    <>
      <DescriptionTitle />
      {
        props.markupEditingEnabled ?
          <FieldCardDetailMarkup markup={props.description} /> :
          <FieldCardDetailText>{props.description}</FieldCardDetailText>
      }
    </>
  );
}

function ColumnDescription(props: { column: Column | UpstreamColumn, markupEditingEnabled: boolean }): JSX.Element {
  const description = props.column.description;

  return (
    <>
      <DescriptionTitle sourceColumn={props.column} />
      {
        props.markupEditingEnabled ?
          <FieldCardDetailMarkup markup={description ?? ''} /> :
          <FieldCardDetailText>{description}</FieldCardDetailText>
      }
    </>
  );
}

function RemoteFieldDescription(props: { remoteField: Field, markupEditingEnabled: boolean }): JSX.Element {
  const description = props.remoteField.description;

  return (
    <>
      <DescriptionTitle sourceField={props.remoteField} />
      {
        props.markupEditingEnabled ?
          <FieldCardDetailMarkup markup={description ?? ''} /> :
          <FieldCardDetailText>{description}</FieldCardDetailText>
      }
    </>
  );
}

function RemoteColumnDescription(props: {
  remoteColumn: Column | UpstreamColumn, remoteField: Field, markupEditingEnabled: boolean
}): JSX.Element {
  const description = props.remoteColumn.description;

  return (
    <>
      <DescriptionTitle sourceColumn={props.remoteColumn} sourceField={props.remoteField} />
      {
        props.markupEditingEnabled ?
          <FieldCardDetailMarkup markup={description ?? ''} /> :
          <FieldCardDetailText>{description}</FieldCardDetailText>
      }
    </>
  );
}

function NoDescription(): JSX.Element {
  return (
    <>
      <DescriptionTitle />
      <NoDescriptionPlaceholder>{Messages.NoDescriptionAvailable()}</NoDescriptionPlaceholder>
    </>
  );
}

function DescriptionTitle(props: { sourceColumn?: Column | UpstreamColumn, sourceField?: Field }): JSX.Element {
  return (
    <FieldCardDetailTitleWrapper>
      <FieldCardDetailTitle>
        {Messages.Description()}
        <DescriptionInfo direction='horizontal' {...props} />
      </FieldCardDetailTitle>
    </FieldCardDetailTitleWrapper>
  );
}
function maybeGetColumnFromField(field: NonDatasourceFieldDetailsFragment): Column | null {
  if (field.__typename === 'ColumnField') {
    const columnsConnection = field.columnsConnection;
    if (columnsConnection) {
      const columns = columnsConnection.nodes;
      if (columns && columns.length > 0) {
        const column = columns[0];
        if (column) {
          return column;
        }
      }
    }
  }
  return null;
}

